import {
  ImageSliceView,
  ImageSliceViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { getCreativeSuffix } from 'src/lib/utils';
import popups from 'src/lib/ui/config/popups';
import * as Pet from 'src/replicant/pet';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import ButtonScaleViewWithText, {
  Opts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import sounds from 'src/lib/sounds';
import * as Duo from 'src/replicant/duo';
import StateObserver from 'src/StateObserver';
import { duration } from 'src/replicant/utils/duration';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';
import {
  startRespondToDuoSequence,
  sendDuoReminder,
  duoPopupMutex,
} from '../duo.sequence';
import { getJSTDate } from 'src/replicant/utils/time';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

type Sent = DuoFriend & {
  type: 'sent';
};
type Received = DuoFriend & {
  type: 'received';
};

namespace style {
  export const container: Omit<ButtonScaleViewOpts, 'tracking'> = {
    offsetX: 20,
    offsetY: 10,
    width: popups.buruburu.width - 100,
    height: 120,
  };

  export const okButton: Omit<Opts, 'tracking'> = {
    ...uiConfig.buttons.secondary,
    imageDisabled: 'assets/ui/shared/buttons/btn_cancel_disabled.png',
    labelOffsetX: -2,
    labelOffsetY: -1,
    fontSize: 25,
    font: 'Body',
    x: 390,
    y: 80,
    width: 140,
    height: 50,
    centerOnOrigin: true,
  };

  export const tile: ImageSliceViewOpts = {
    image: 'assets/ui/shared/frames/scroller_panel_blue.png',
    width: container.width,
    height: container.height,
    slices: {
      top: 19,
      left: 19,
      right: 22,
      bot: 28,
    },
  };

  export const image: ImageScaleViewOpts = {
    scaleMethod: 'contain',
    width: 78,
    height: 78,
    x: 16,
    y: 15,
  };

  export const name: LangBitmapOpts = {
    size: 30,
    font: 'Body',
    color: '#9A7171',
    verticalAlign: 'top',
    width: 208,
    height: 48,
    x: 112,
    y: 20,
  };

  export const speciesLabel: LangBitmapOpts = {
    size: 22,
    font: 'Body',
    color: '#7191AB',
    x: name.x,
    y: 64,
    width: 155,
    height: 34,
    align: 'center',
    verticalAlign: 'center',
  };

  export const speciesIconBackdrop: ImageViewOpts = {
    image: 'assets/ui/shared/frames/backdrop_panel.png',
    x: image.x! + image.width!,
    y: image.y! + image.height!,
    width: 128,
    height: 133,
    scale: 0.33,
  };
  speciesIconBackdrop.x! -=
    speciesIconBackdrop.width! * speciesIconBackdrop.scale! * 0.7;
  speciesIconBackdrop.y! -=
    speciesIconBackdrop.height! * speciesIconBackdrop.scale! * 0.9;

  export const speciesIcon: ImageScaleViewOpts = {
    width: speciesIconBackdrop.width,
    height: speciesIconBackdrop.height,
    scaleMethod: 'contain',
  };

  export const expiration: LangBitmapOpts = {
    font: speciesLabel.font,
    color: speciesLabel.color,
    x: 307,
    y: 1,
    height: 60,
    width: 160,
    size: 15,
    align: 'center',
    verticalAlign: 'center',
  };
}

export class DuoFriendTile {
  public readonly view: ButtonScaleView;

  constructor({
    friend,
    close,
  }: {
    friend: Sent | Received;
    close: (closeMenu: boolean) => void;
  }) {
    this.view = new ButtonScaleView({
      ...style.container,
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS.SELECT,
      },
    });

    // background
    this.view.addSubview(new ImageSliceView(style.tile));

    // icon
    this.view.addSubview(
      new ImageScaleView({
        ...style.image,
        image: friend.profile.photo || GENERIC_AVATAR,
      }),
    );

    // name
    this.view.addSubview(
      new LangBitmapFontTextView({
        ...style.name,
        width: style.container.width! - 268,
        localeText: () => friend.profile.name || '',
      }),
    );

    // species name
    const { user } = StateObserver.getState();
    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];
    const { species } = friend.pet;
    this.view.addSubview(
      new LangBitmapFontTextView({
        ...style.speciesLabel,
        localeText: () => evolutions[species]?.[`name_${getCreativeSuffix()}`],
      }),
    );

    // species icon
    const speciesIcon = this.view.addSubview(
      new ImageView(style.speciesIconBackdrop),
    );
    speciesIcon.addSubview(
      new ImageScaleView({
        ...style.speciesIcon,
        image: `assets/species/${species}/icon.png`,
      }),
    );

    // expiration
    const elapsedTime = StateObserver.now() - friend.invitation.timestamp;
    const expiration = Math.max(0, Duo.ruleset.inviteExpiration - elapsedTime);
    const hh = Math.floor(expiration / duration({ hours: 1 }));
    const mm = Math.floor(
      (expiration % duration({ hours: 1 })) / duration({ minutes: 1 }),
    );

    const showExpiration =
      friend.invitation.status === 'pending' && expiration !== 0;

    if (showExpiration) {
      this.view.addSubview(
        new LangBitmapFontTextView({
          ...style.expiration,
          localeText: () => i18n('duo.popup.expires', { hh, mm }),
        }),
      );
    }

    const button = new ButtonScaleViewWithText({
      ...style.okButton,
      y: showExpiration ? style.okButton.y : 60,
      localeText: () =>
        i18n(`duo.popup.${friend.type === 'sent' ? 'remind' : 'respond'}`),
      onClick: async () => {
        try {
          sounds.sfx.play('ok');

          close(friend.type === 'received');

          if (friend.type === 'sent') {
            await sendDuoReminder(friend);

            return;
          }

          await startRespondToDuoSequence(friend);
        } finally {
          duoPopupMutex.release();
        }
      },
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS.REMIND,
      },
    });

    this.view.addSubview(button);

    let canRemind = true;

    if (friend.type === 'sent') {
      const jstDay = getJSTDate(StateObserver.now());
      jstDay.setHours(0, 0, 0, 0);

      const jstLastReminderDay = getJSTDate(friend.invitation.lastReminder);
      jstLastReminderDay.setHours(0, 0, 0, 0);

      canRemind = jstDay.getTime() !== jstLastReminderDay.getTime();
    }

    const disabled =
      expiration === 0 || // Should disable if expired.
      !canRemind || // Should disable if already reminded today.
      friend.invitation.status !== 'pending'; // Should disable if already responded.

    // Set status text when disabled.
    if (disabled) {
      if (friend.invitation.status === 'declined') {
        button.localeText = () => i18n('duo.popup.declined');
      } else if (friend.invitation.status === 'cancelled') {
        button.localeText = () => i18n('duo.popup.cancelled');
      } else if (expiration === 0) {
        button.localeText = () => i18n('duo.popup.expired');
      } else if (!canRemind) {
        button.disabledImage =
          'assets/ui/shared/buttons/btn_secondary_disabled.png';
        button.localeText = () => i18n(`duo.popup.reminded`);
      }
    }

    button.setDisabled(disabled);

    const item = friend.invitation
      .item as keyof typeof uiConfig.buruburu.care.itemButton.icons;

    this.view.addSubview(
      new ImageScaleView({
        image: uiConfig.buruburu.care.itemButton.icons[item],
        scaleMethod: 'contain',
        x: 249,
        y: 31,
        width: 80,
        height: 50,
      }),
    );
  }
}
